import {clamp} from 'lodash';

/**
 * Represents the open canvas viewport position (on the x, y axes) and scale (k)
 * This is a standard representation of a translate & scale transformation used for panning & zooming
 * and matches the transform representation used by [`d3-zoom`](https://github.com/d3/d3-zoom/blob/main/src/transform.js).
 *
 * The viewport is represented in world space, meaning the viewport's position is relative to a global origin point (0, 0)
 * of an infinite canvas coordinate system.
 */
export type Viewport = {
  /**
   * Position of the viewport on the x axis
   */
  x: number;
  /**
   * Position of the viewport on the y axis
   */
  y: number;
  /**
   * Viewport scale value
   */
  k: number;
};

/**
 * Default viewport scale below which the toolbars of non-active, non-selected
 * frames are hidden, to avoid frame outlines overlapping when zoomed out.
 *
 * This is the shared fallback returned by
 * `OpenCanvas.selectToolbarVisibilityZoomLimit` when the active feature does
 * not configure its own `toolbarVisibilityZoomLimit`. Component Canvas relies
 * on this default; Multi Context Canvas lowers it via its feature config.
 */
export const VIEWPORT_ZOOM_TOOLBAR_VISIBILITY_LIMIT = 0.4;

/**
 * Returns whether toolbars of inactive, non-selected frames should be visible
 * at the given viewport scale.
 *
 * @param viewport - The current viewport
 * @param limit - The feature's zoom limit. Each feature passes its own limit
 *   constant from its callsite (e.g. `MCC_TOOLBAR_VISIBILITY_ZOOM_LIMIT`).
 */
export const getIsShowingInactiveToolbars = (
  viewport: Viewport,
  limit: number
) => viewport.k >= limit;

/**
 * Pans the viewport by the given `dx` and `dy`
 *
 * @param viewport - The current viewport
 * @param dx - The amount to pan on the x axis, in screen pixels
 * @param dy - The amount to pan on the y axis, in screen pixels
 * @returns The updated viewport transform
 */
export const panViewport = (
  viewport: Viewport,
  dx: number,
  dy: number
): Viewport => {
  if (dx === 0 && dy === 0) return viewport;

  const {x, y, k} = viewport;

  // Convert screen pixels deltas to world units
  const worldDx = dx / k;
  const worldDy = dy / k;

  return {
    k,
    x: x + worldDx,
    y: y + worldDy,
  };
};

/**
 * Pans the viewport to a given point in screen space, centering the point at the given `viewportCenter`.
 *
 * @param viewport - The current viewport
 * @param screenPoint - The point to pan to, in screen space
 * @param viewportCenter - The viewport center, in screen space
 * @returns the updated viewport transform
 */
export const panViewportCenterToPoint = (
  viewport: Viewport,
  screenPoint: Point,
  viewportCenter: Point
): Viewport => {
  const [screenX, screenY] = screenPoint;
  const [screenViewportCenterX, screenViewportCenterY] = viewportCenter;

  const dx = screenX - screenViewportCenterX;
  const dy = screenY - screenViewportCenterY;

  return panViewport(viewport, dx, dy);
};

/**
 * Represents a point in 2D space, either in screen or world space
 */
export type Point = [x: number, y: number];

/**
 * Converts the given `x` and `y` numbers to a `Point`
 * @returns Point
 */
export const toPoint = (x: number, y: number): Point => [x, y];

/**
 * Converts a point in screen space to its coordinates in world space
 * @param point - a point in screen space
 * @param viewport - The current viewport
 */
export const screenToWorldPoint = (point: Point, viewport: Viewport): Point => {
  const [pointX, pointY] = point;

  const worldX = viewport.x + pointX / viewport.k;
  const worldY = viewport.y + pointY / viewport.k;

  return [worldX, worldY];
};

/**
 * Converts a point in world space to its coordinates in screen space
 * @param point - a point in world space
 * @param viewport - The current viewport
 */
export const worldToScreenPoint = (point: Point, viewport: Viewport): Point => {
  const [pointX, pointY] = point;

  const screenX = (pointX - viewport.x) * viewport.k;
  const screenY = (pointY - viewport.y) * viewport.k;

  return [screenX, screenY];
};

/**
 * The minimum zoom factor that can be produced by mouse wheel events
 */
export const MIN_ZOOM_FACTOR = 0.01;

/**
 * The minimum zoom level the open canvas can be set to (2%)
 */
export const MIN_ZOOM_LEVEL = 0.02;

/**
 * The maximum zoom level the open canvas can be set to (400%)
 */
export const MAX_ZOOM_LEVEL = 4;

/**
 * Given the current `Viewport` and a `zoomFactor`, clamps the `zoomFactor` value
 * based on the `MIN_ZOOM_LEVEL` and `MAX_ZOOM_LEVEL` values.
 */
const clampZoomFactor = (viewport: Viewport, zoomFactor: number): number => {
  const maxZoomFactor = MAX_ZOOM_LEVEL / viewport.k;
  const minZoomFactor = MIN_ZOOM_LEVEL / viewport.k;
  return clamp(zoomFactor, minZoomFactor, maxZoomFactor);
};

/**
 * Sets the zoom of the viewport to a given value.
 *
 * The function clamps the provided `zoomFactor` value based on  `MIN_ZOOM_LEVEL` and `MAX_ZOOM_LEVEL`.
 *
 * @param viewport - The current viewport
 * @param zoomFactor - The multiplier by which to change the viewport zoom (example: 2.0 to zoom in 2x)
 * @param origin - The point around which the zoom is performed, in screen space
 */
export const zoomViewport = (
  viewport: Viewport,
  zoomFactor: number,
  originPoint: Point
): Viewport => {
  if (zoomFactor === 1) return viewport;

  // Convert the origin point from screen space to world space.
  // This represents the world coordinate under the mouse before zoom
  const [originWorldX, originWorldY] = screenToWorldPoint(
    originPoint,
    viewport
  );

  // Clamp zoom factor to limit the change that can be applied when zooming
  const clampedZoomFactor = clampZoomFactor(viewport, zoomFactor);

  // Compute next zoom (k) value
  const newK = viewport.k * clampedZoomFactor;

  /**
   * Compute the `(newX, newY)` viewport position after zooming to ensure the viewport
   * remains at the origin screen point `(pointX, pointY)`.
   *
   * For the x coordinate: given the screen point `(pointX, pointY)` we compute `originWorldX` as:
   *
   * ```
   * originWorldX = viewport.x + pointX / viewport.k
   * ```
   *
   * We are looking for `newX` as the viewport's next x coordinate at zoom `newK` such that:
   *
   * ```
   * originWorldX = newX + pointX / newK
   * ```
   *
   * Solving for `newX` we get:
   *
   * ```
   * newX = originWorldX - pointX / newK
   * ```
   *
   * The same computation applies for the y coordinate to compute `newY`
   */
  const [pointX, pointY] = originPoint;
  const newX = originWorldX - pointX / newK;
  const newY = originWorldY - pointY / newK;

  return {
    k: newK,
    x: newX,
    y: newY,
  };
};

export const VIEWPORT_ZOOM_STEPS = [
  MIN_ZOOM_LEVEL,
  0.03,
  0.06,
  0.13,
  0.25,
  0.5,
  1,
  2,
  MAX_ZOOM_LEVEL,
] as const;

/**
 * Given a current zoom value (i.e. viewport.k), return the next zoom step in the given direction
 * If the next zoom step is not found (i.e. we are at the maximum or minimum zoom step), return null
 */
export const getNextZoomStep = (
  currentZoom: number,
  direction: 'in' | 'out'
): number | null => {
  if (direction === 'in') {
    // Find the first step that is greater than currentZoom
    const nextStep = VIEWPORT_ZOOM_STEPS.find((step) => step > currentZoom);
    return nextStep ?? null;
  } else {
    // Find the last step that is less than currentZoom
    const prevSteps = VIEWPORT_ZOOM_STEPS.filter((step) => step < currentZoom);
    return prevSteps.at(-1) ?? null;
  }
};

/**
 * Given a current zoom value (i.e. viewport.k) and a next zoom value,
 * compute the factor by which the current zoom value needs to change to get to the next zoom value.
 */
export const getZoomFactor = (
  currentZoom: number,
  nextZoom: number
): number => {
  if (currentZoom === 0) return 1;

  return nextZoom / currentZoom;
};

/**
 * Returns whether a zoom interaction can change the current zoom level.
 *
 * A zoom factor of 1 is a no-op. Zooming out at minimum zoom and zooming in
 * at maximum zoom are also no-ops.
 */
export const canApplyZoomFactor = (
  currentZoom: number,
  zoomFactor: number
): boolean => {
  return (
    zoomFactor !== 1 &&
    currentZoom >= MIN_ZOOM_LEVEL &&
    currentZoom <= MAX_ZOOM_LEVEL
  );
};
type FrameRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type ViewportDimensions = {
  width: number;
  height: number;
};

// These margins have been hardcoded and don't use the frame size on purpose.
// converting the frame's design token to a pixel value proved to be flaky,
// Spring team suggested just hardcoding them.
const FIT_TO_VIEW_STANDARD_MARGIN = 20;
const FIT_TO_VIEW_TOP_MARGIN = 52;

const FIT_TO_VIEW_MARGINS = {
  left: FIT_TO_VIEW_STANDARD_MARGIN,
  right: FIT_TO_VIEW_STANDARD_MARGIN,
  top: FIT_TO_VIEW_TOP_MARGIN,
  bottom: FIT_TO_VIEW_STANDARD_MARGIN,
} as const;

/**
 * Computes a viewport that fits a frame into the viewport while preserving
 * margins on all four sides.
 */
export const getViewportForFitToView = ({
  viewport,
  frameRect,
  viewportDimensions,
}: {
  viewport: Viewport;
  frameRect: FrameRect;
  viewportDimensions: ViewportDimensions;
}): Viewport | null => {
  const {
    left: leftMargin,
    right: rightMargin,
    top: topMargin,
    bottom: bottomMargin,
  } = FIT_TO_VIEW_MARGINS;

  const {left, top, width: frameWidth, height: frameHeight} = frameRect;
  const {width: viewportWidth, height: viewportHeight} = viewportDimensions;

  if (
    frameHeight <= 0 ||
    frameWidth <= 0 ||
    viewportHeight <= 0 ||
    viewportWidth <= 0 ||
    viewport.k <= 0
  ) {
    return null;
  }

  const usableHeight = Math.max(viewportHeight - topMargin - bottomMargin, 0);
  const usableWidth = Math.max(viewportWidth - leftMargin - rightMargin, 0);
  if (usableHeight <= 0 || usableWidth <= 0) return null;

  const frameHeightInWorld = frameHeight / viewport.k;
  const frameWidthInWorld = frameWidth / viewport.k;

  const zoomToFitHeight = usableHeight / frameHeightInWorld;
  const zoomToFitWidth = usableWidth / frameWidthInWorld;

  const targetZoom = clamp(
    Math.min(zoomToFitHeight, zoomToFitWidth),
    MIN_ZOOM_LEVEL,
    MAX_ZOOM_LEVEL
  );

  const [frameLeftWorldX, frameWorldY] = screenToWorldPoint(
    [left, top],
    viewport
  );
  const [frameRightWorldX] = screenToWorldPoint(
    [left + frameWidth, top],
    viewport
  );
  const frameCenterWorldX = (frameLeftWorldX + frameRightWorldX) / 2;
  const viewportContentCenterX = leftMargin + usableWidth / 2;

  const result = {
    k: targetZoom,
    x: frameCenterWorldX - viewportContentCenterX / targetZoom,
    y: frameWorldY - topMargin / targetZoom,
  };

  return result;
};
